<template>
  <lf-modal
    :title="
      $t(
        'DEALS.DEAL_PROGRESS.BUSINESS_INFO.ADDITIONAL_BUSINESS_INFO.TITLE_EDIT'
      )
    "
    :close="close"
  >
    <template #content>
      <div class="p-6">
        <form novalidate @submit="onSubmit">
          <additional-business-info-fields
            :data="data"
            @industry-type-change="onIndustryTypeChange"
            @website-address-change="handleWebsiteAddressChange"
          />
          <hr class="my-4" />
          <div class="flex justify-end">
            <outline-button class="mr-3" @click="close">
              {{ $t("COMMON.CANCEL") }}
            </outline-button>
            <primary-button type="submit" :disabled="isSubmitting">
              {{ $t("COMMON.SAVE") }}
            </primary-button>
          </div>
        </form>
      </div>
    </template>
  </lf-modal>
</template>
<script setup lang="ts">
import { useForm } from "vee-validate";

import { looksLikeNumber } from "@/helpers/common";
import { FULL_MONTHS } from "@/helpers/constants";
import {
  filterNotNullableValues,
  forceNullForEmptyStrings,
  getDateFromMonth,
  looksLikeCurrency,
} from "@/helpers/formatting";
import { callStoreAction } from "@/helpers/vee-validate";
import { websiteAddressChange } from "@/helpers/workflow";
import { useNumberOfEmployees } from "@/hooks/options";
import useBusinessesStore from "@/stores/businesses";

import AdditionalBusinessInfoFields from "@/components/entityProfiles/cards/AdditionalBusinessInfoFields.vue";
import LfModal from "@/components/ui/Modal.vue";

import type { IBusinessEntityProfile } from "@/models/businesses";
import type { PropType } from "vue";

const props = defineProps({
  data: {
    type: Object as PropType<IBusinessEntityProfile | null>,
  },
  close: {
    type: Function as PropType<() => void>,
    required: true,
  },
});

const DEFAULT_MONTH = 1;

const businessesStore = useBusinessesStore();
const { numberOfEmployeeIdByIndex } = useNumberOfEmployees();

const { handleSubmit, isSubmitting, setFieldValue } = useForm({
  initialValues: {
    ...props.data,
    fiscal_year_end: FULL_MONTHS[props.data?.fiscal_year_end || DEFAULT_MONTH],
  },
});

const onIndustryTypeChange = () => {
  setFieldValue("sub_industry_type_id", undefined);
};

const onSubmit = handleSubmit(async (values, actions) => {
  if (looksLikeNumber(values?.number_of_employees_id)) {
    values.number_of_employees_id = numberOfEmployeeIdByIndex(
      Number(values.number_of_employees_id)
    );
  }

  const currencyValueFields: (keyof typeof values)[] = [
    "stated_average_balance",
    "current_business_debt",
  ];

  currencyValueFields.forEach((field) => {
    if (values[field] && looksLikeCurrency(String(values[field]))) {
      (values[field] as number) = Number(
        String(values[field]).replace(/[^\d.]/g, "")
      );
    }
  });

  values = filterNotNullableValues(values, Object.keys(values));
  values = forceNullForEmptyStrings(values);

  if (values?.fiscal_year_end) {
    values.fiscal_year_end = getDateFromMonth(values.fiscal_year_end) || "";
  }

  await callStoreAction(
    {
      profileId: props?.data?.id,
      params: values,
    },
    actions,
    "updateBusinessEntityProfile",
    businessesStore
  );

  props.close();
});

const handleWebsiteAddressChange = (e: Event) => {
  setFieldValue("website_address", websiteAddressChange(e));
};
</script>
